import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { Alert } from 'react-native';
import { PageFormat } from '~/data/types/graphql';
import { t } from '~/utils/i18n';
import {
  FileDetails,
  FileOptions,
  GetFileWithDetailsFromLibrary,
  GetImageFromCamera,
  GetImageFromLibrary,
} from './types';
import { getFileDetail } from './utils';

export {
  FileOptions,
  FileDetails,
  GetFileWithDetailsFromLibrary,
  GetImageFromCamera,
  GetImageFromLibrary,
  getFileDetail,
};

async function launchImageLibrary(options?: FileOptions): Promise<string[]> {
  const allowsMultipleSelection = !options?.circleCrop;
  const selectionLimit = options?.maxFiles || 1;

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ['images'],
    quality: options?.compressImageQuality ?? 0.8,
    allowsMultipleSelection,
    selectionLimit,
  });

  if (!result.canceled) {
    return result.assets.map((asset) => asset.uri);
  }
  return [];
}

export const getImageFromLibrary: GetImageFromLibrary = async (
  options?: FileOptions,
) => {
  return launchImageLibrary(options);
};

export const getImageFromCamera: GetImageFromCamera = async (
  options?: FileOptions,
) => {
  return launchImageLibrary(options);
};

export const getImageWithDetailsFromLibrary = async (
  pageFormat?: PageFormat,
): Promise<FileDetails[]> => {
  const uris = await launchImageLibrary();
  return Promise.all(
    uris.map((uri) => getFileDetail({ uri, type: 'image/jpeg', pageFormat })),
  );
};

export const getFileWithDetailsFromLibrary: GetFileWithDetailsFromLibrary =
  async (pageFormat) => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
      });

      const files = result.assets ?? [];

      const detailedFiles = await Promise.all(
        files.map(async (file) => {
          return await getFileDetail({
            ...file,
            uri: file.uri,
            type: file.mimeType,
            pageFormat,
          });
        }),
      );
      return detailedFiles;
    } catch (error) {
      if (error instanceof Error && error.message.includes('cancelled')) {
        Alert.alert(t('g.operationCancelled'));
      } else {
        Alert.alert(t('g.unexpectedErrorOccurred'));
      }
      return [];
    }
  };
